import { ReactElement, useCallback } from 'react';
import { AxiosResultDefaultError } from '../../../../api/request';
import { AuthenticationError } from '../../../../api/requests/tuitionProcess';
import { createFetchContext } from '../../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../../components/info/DisplayError';
import Loading from '../../../../components/info/Loading';
import { getPeriods } from '../api';
import { PerformanceAnalysisPeriodsResponse, Period } from '../types';

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  undefined,
  PerformanceAnalysisPeriodsResponse,
  AuthenticationError | AxiosResultDefaultError
>();

export function usePerformanceAnalysisPeriodLoader() {
  const result = useFetch();
  if (result.error) {
    throw new Error(
      'Ha ocurrido un error. Deberías usar este hook solo si puedes asegurar que no hay error',
    );
  }

  if (!result.data) {
    throw new Error(
      'No hay data. Deberías usar este hook solo si puedes asegurar que hay data',
    );
  }

  return result.data;
}

export const PerformanceAnalysisPeriodConsumer = FetchConsumer;

export function PerformanceAnalysisPeriodLoader({
  children,
}: {
  children: (period: Period) => ReactElement;
}) {
  const request = useCallback(async () => {
    return getPeriods();
  }, []);

  return (
    <FetchProvider request={request} defaultQuery={undefined} fetchImmediately>
      <FetchConsumer>
        {({ data, error, loading, refresh }) => {
          if (error) {
            return (
              <DisplayError
                insideCard
                textBody={error.code}
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }

          if (loading) {
            return <Loading insideCard />;
          }

          if (!data) {
            return (
              <DisplayError
                insideCard
                textBody="Data no cargada"
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }

          if (!data.periodDefault) {
            return (
              <DisplayError
                insideCard
                textBody="Siempre debería haber un periodo por defecto"
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }

          return children(data.periodDefault);
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
